import { useState } from "react";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import { ArrowRight, PenLine } from "lucide-react";

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH.
const startGoogleAuth = () => {
  const redirectUrl = window.location.origin + "/";
  window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
};

export default function AuthPage() {
  const { login, register } = useAuth();
  const [mode, setMode] = useState("login");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      if (mode === "login") await login(email, password);
      else await register(name, email, password);
      toast.success(mode === "login" ? "Welcome back" : "Account created");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Something went wrong");
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      {/* Left */}
      <div className="hidden lg:flex flex-col justify-between p-12 hair-r relative overflow-hidden">
        <div className="flex items-center gap-2">
          <PenLine className="w-5 h-5" style={{ color: "var(--primary)" }} />
          <span className="mono-label" style={{ color: "var(--ink)" }}>Scriptora</span>
        </div>
        <div>
          <h1 className="font-serif-display text-6xl xl:text-7xl leading-[0.95] tracking-tight" data-testid="auth-hero-title">
            Write like<br />
            <span style={{ color: "var(--primary)" }}>them.</span><br />
            <span className="italic">Not like a bot.</span>
          </h1>
          <p className="mt-8 text-base max-w-md" style={{ color: "var(--ink-2)" }}>
            A ghostwriter&apos;s tool for feeding each client&apos;s true voice into every script — video, email, social, or sales.
          </p>
        </div>
        <div className="mono-label">Est. 2026 · A tool for the pen</div>
      </div>

      {/* Right */}
      <div className="flex items-center justify-center p-6 sm:p-12">
        <form onSubmit={submit} className="w-full max-w-md" data-testid="auth-form">
          <div className="mb-8">
            <div className="mono-label mb-3">{mode === "login" ? "Return" : "Begin"}</div>
            <h2 className="font-serif-display text-4xl tracking-tight">
              {mode === "login" ? "Sign in" : "Create your studio"}
            </h2>
          </div>

          {mode === "register" && (
            <label className="block mb-5">
              <span className="mono-label block mb-2">Name</span>
              <input
                data-testid="auth-name-input"
                required
                value={name} onChange={(e) => setName(e.target.value)}
                className="w-full surface hair px-4 py-3 rounded-md focus-primary"
                placeholder="Isla Whitmore"
              />
            </label>
          )}          <label className="block mb-5">
            <span className="mono-label block mb-2">Email</span>
            <input
              data-testid="auth-email-input"
              required type="email"
              value={email} onChange={(e) => setEmail(e.target.value)}
              className="w-full surface hair px-4 py-3 rounded-md focus-primary"
              placeholder="you@studio.co"
            />
          </label>

          <label className="block mb-8">
            <span className="mono-label block mb-2">Password</span>
            <input
              data-testid="auth-password-input"
              required type="password" minLength={6}
              value={password} onChange={(e) => setPassword(e.target.value)}
              className="w-full surface hair px-4 py-3 rounded-md focus-primary"
              placeholder="••••••••"
            />
          </label>

          <button
            data-testid="auth-submit-button"
            disabled={busy}
            className="btn-primary w-full flex items-center justify-center gap-2 py-3 rounded-md font-medium"
          >
            {busy ? "Working…" : (mode === "login" ? "Sign in" : "Create account")}
            <ArrowRight className="w-4 h-4" />
          </button>

          <div className="flex items-center gap-3 my-6">
            <div className="flex-1 h-px" style={{ background: "var(--hair)" }} />
            <span className="mono-label" style={{ color: "var(--ink-2)" }}>or</span>
            <div className="flex-1 h-px" style={{ background: "var(--hair)" }} />
          </div>

          <button
            type="button"
            data-testid="auth-google-button"
            onClick={startGoogleAuth}
            className="w-full hair rounded-md py-3 flex items-center justify-center gap-3 text-sm font-medium transition-colors duration-200 hover:bg-[var(--surface-hover)]"
            style={{ background: "var(--paper)" }}
          >
            <GoogleIcon /> Continue with Google
          </button>

          <button
            type="button"
            data-testid="auth-toggle-mode"
            onClick={() => setMode(mode === "login" ? "register" : "login")}
            className="mt-6 mono-label hover:text-black transition-colors"
          >
            {mode === "login" ? "No account? Create one →" : "Already registered? Sign in →"}
          </button>
        </form>
      </div>
    </div>
  );
}

function GoogleIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 48 48" aria-hidden="true">
      <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/>
      <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/>
      <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/>
      <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/>
    </svg>
  );
}
